import type { Images, imageSrcs } from 'src/shared/frontendTypes/images'
import type { discountPrice } from 'src/shared/frontendTypes/product'
import PriceDisplay from 'src/components/PriceDisplay'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import getImageUrl from '../../../functions/imageUrl'
import getImageAlt from '../../../functions/imageAlt'
import styles from '../../../ProductPresentation.module.css'

interface Props {
  productId: number
  name: string
  images: Images | imageSrcs
  price?: number | discountPrice
  fromPrice: boolean
  selection: string
  onlyStock?: boolean
  stock?: number
  dialog?: boolean
  suspended?: boolean
  onOpenImage: () => void
}

/** The same variant and price as the gallery/purchase form, kept in view on mobile. */
export default function PurchasePreview({ productId, name, images, price, fromPrice,
  selection, onlyStock, stock, dialog = false, suspended = false, onOpenImage }: Props) {
  const inline = useRef<HTMLDivElement>(null)
  const start = useRef<HTMLSpanElement>(null)
  const [pinned, setPinned] = useState(false)
  const [floatingStyle, setFloatingStyle] = useState<CSSProperties>()

  useEffect(() => {
    const element = inline.current
    const marker = start.current
    const panel = element?.closest('[data-product-purchase-panel]')
    if (dialog || !element || !marker || !panel || typeof IntersectionObserver === 'undefined') return
    const header = document.querySelector('header')
    let startObserver: IntersectionObserver | undefined
    let endObserver: IntersectionObserver | undefined
    let observedSizes = ''
    let headerVisible = false
    let pastStart = false
    let beforeEnd = true
    const publish = () => setPinned(window.innerWidth < 960 && pastStart && beforeEnd)
    const measure = () => {
      const headerRect = header?.getBoundingClientRect()
      headerVisible = Boolean(headerRect && headerRect.bottom > 0)
      const headerHeight = headerVisible ? Math.max(0, Math.ceil(headerRect?.height || 0)) : 0
      const rect = element.getBoundingClientRect()
      const height = Math.ceil(rect.height)
      const computed = getComputedStyle(element)
      const next: CSSProperties = {
        top: headerHeight, left: rect.left, width: rect.width,
        fontFamily: computed.fontFamily, color: computed.color,
        ...Object.fromEntries(['--page-builder-text', '--page-builder-accent', '--page-builder-purchase-button-bg']
          .map(key => [key, computed.getPropertyValue(key)])),
      }
      setFloatingStyle(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next)
      pastStart = marker.getBoundingClientRect().top < headerHeight
      beforeEnd = panel.getBoundingClientRect().bottom > headerHeight + height
      publish()
      const sizes = `${headerHeight}:${height}`
      if (observedSizes === sizes) return
      observedSizes = sizes
      startObserver?.disconnect()
      endObserver?.disconnect()
      startObserver = new IntersectionObserver(([entry]) => {
        pastStart = entry.boundingClientRect.top < headerHeight
        publish()
      }, { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: [0, 1] })
      endObserver = new IntersectionObserver(([entry]) => {
        beforeEnd = entry.boundingClientRect.bottom > headerHeight + height
        publish()
      }, { rootMargin: `-${headerHeight + height}px 0px 0px 0px`, threshold: 0 })
      startObserver.observe(marker)
      endObserver.observe(panel)
    }
    measure()
    // A scrolling header needs no reserved space once it has left the viewport.
    // Observe that boundary only; partial visibility changes never add scroll work.
    const headerObserver = header ? new IntersectionObserver(([entry]) => {
      if (entry && (entry.boundingClientRect.bottom > 0) !== headerVisible) measure()
    }, { threshold: [0, 1] }) : null
    if (header) headerObserver?.observe(header)
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    resizeObserver?.observe(element)
    if (header) resizeObserver?.observe(header)
    window.addEventListener('resize', measure)
    return () => {
      startObserver?.disconnect()
      endObserver?.disconnect()
      headerObserver?.disconnect()
      resizeObserver?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [dialog])

  const firstImage = images[0]
  // Reuse the gallery URL: changing a colour does not fetch a second thumbnail.
  const src = firstImage && getImageUrl(firstImage, productId, 'md')
  const smallSrc = firstImage && getImageUrl(firstImage, productId, 'sm')
  const content = <>
    {src && <button type='button' className={styles.variantImage} onClick={onOpenImage}
      aria-label={`Bild der Auswahl vergrößern: ${selection || name}`}>
      <picture>
        {smallSrc && <source media='(max-width: 382px) and (max-resolution: 1dppx)' srcSet={smallSrc} />}
        <img key={src} src={src} alt={getImageAlt(firstImage, name)} width={92} height={92} decoding='async' loading='lazy' />
      </picture>
      <span className={styles.imageHint} aria-hidden='true'>+</span>
      <AiGeneratedBadge aiGenerated={firstImage.aiGenerated} size='compact' />
    </button>}
    <div className={styles.previewDetails}>
      {fromPrice && <span className={styles.fromPrice}>Ab</span>}
      {price !== undefined ? <PriceDisplay price={price} component='div' variant='h4' align='left'
        layout='purchase' showTaxHint onlyStock={onlyStock} stock={stock} /> : <strong>Preis auf Anfrage</strong>}
      {selection && <p className={styles.previewSelection}>{selection}</p>}
    </div>
  </>
  const floating = pinned && !suspended && !dialog
  return <>
    <div ref={inline} className={dialog ? styles.dialogPreview : styles.priceBlock}
      data-purchase-preview={dialog ? 'dialog' : 'inline'}
      style={floating ? { visibility: 'hidden' } : undefined} aria-hidden={floating || undefined}>
      {!dialog && <span ref={start} className={styles.previewStart} aria-hidden='true' />}
      {content}
    </div>
    {/* Keep the original dimensions in the document. A body portal avoids WebKit's
        jittering sticky layers inside the storefront's clipped layout ancestors. */}
    {floating && floatingStyle && createPortal(<div className={`${styles.priceBlock} ${styles.floatingPreview}`}
      style={floatingStyle} data-purchase-preview='floating'>{content}</div>, document.body)}
  </>
}
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
